import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Clock, Bookmark, RefreshCw, Radio, ThumbsUp, ThumbsDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { getFirestore, collection, addDoc, getDocs, deleteDoc, serverTimestamp } from 'firebase/firestore';

const SkeletonSwipeCard = ({ isMobile }) => (
  <div style={{
    position: isMobile ? 'absolute' : 'relative',
    top: isMobile ? 0 : 'auto',
    left: isMobile ? '50%' : 'auto',
    transform: isMobile ? 'translateX(-50%)' : 'none',
    width: isMobile ? 'calc(100% - 2px)' : '100%',
    minWidth: '320px',
    maxWidth: '390px',
    height: '360px',
    zIndex: isMobile ? 10 : 'auto',
    flexShrink: isMobile ? 'initial' : 0,
    background: 'rgba(255, 255, 255, 0.31)',
    backdropFilter: 'blur(20px)',
    borderRadius: '18px',
    padding: '4px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(255, 255, 255, 0.93)'
  }}>
    <div style={{
      width: '100%',
      height: '100%',
      background: 'white',
      borderRadius: '15px',
      border: '1px solid rgba(213, 213, 213, 0.5)',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{ 
        width: '100%', 
        height: '180px',
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite'
      }} />
      
      <div style={{ padding: '14px', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ 
          height: '16px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '4px',
          width: '90%'
        }} />
        <div style={{ 
          height: '16px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '4px',
          width: '70%'
        }} />
        
        <div style={{ 
          height: '12px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '4px',
          width: '95%',
          marginTop: '4px'
        }} />
        <div style={{ 
          height: '12px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '4px',
          width: '60%'
        }} />
        
        <div style={{ 
          marginTop: 'auto',
          height: '36px', 
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
          borderRadius: '18px'
        }} />
      </div>
    </div>
  </div>
);

const SwipeableNewsCards = ({ 
  newsData = {}, 
  savedArticles = [],
  selectedNewsFeeds = [],
  onSaveArticle = () => {},
  onRemoveArticle = () => {},
  isArticleSaved = () => false,
  onViewSource = () => {},
  onRefreshNews = () => {},
  isRefreshing = false,
  onTuneIn = () => {},
  firestoreService = null,
  analyzeArticleWithAI = null
}) => {
  // Mobile-specific states (original swipe behavior)
  const [currentSourceIndex, setCurrentSourceIndex] = useState(0);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const dragStateRef = useRef({
    isDragging: false,
    dragStart: null,
    dragStartY: null,
    dragOffset: 0
  });
  const [visualDragOffset, setVisualDragOffset] = useState(0);
  const [isDraggingVisual, setIsDraggingVisual] = useState(false);
  const animationFrameRef = useRef(null);
  const lastUpdateTimeRef = useRef(0);
  
  // Desktop-specific states (horizontal scroll)
  const [currentStoryIndices, setCurrentStoryIndices] = useState({});
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // Common states
  const [loadingState, setLoadingState] = useState('initializing');
  const [retryCount, setRetryCount] = useState(0);
  const [thumbsFeedback, setThumbsFeedback] = useState({});
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(true);
  const stuckDetectionRef = useRef(null);
  const refreshTimeoutRef = useRef(null);

  // Detect if mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper to get feedback for current card
  const getCardFeedback = (storyUrl) => {
    return thumbsFeedback?.[storyUrl] || null;
  };

  // Update feedback for this card
  const setCardFeedback = async (storyUrl, feedback, storyData) => {
    const currentFeedback = thumbsFeedback?.[storyUrl];
    
    if (currentFeedback === feedback) {
      console.log('🗑️ Removing feedback for:', storyUrl);
      
      setThumbsFeedback(prev => {
        const updated = { ...prev };
        delete updated[storyUrl];
        return updated;
      });
      
      if (firestoreService?.isReady() && storyData) {
        try {
          const db = getFirestore();
          const feedbackRef = collection(
            db,
            'users',
            firestoreService.currentUser.uid,
            'newsFeedback'
          );
          
          const snapshot = await getDocs(feedbackRef);
          snapshot.forEach((doc) => {
            if (doc.data().storyUrl === storyUrl) {
              deleteDoc(doc.ref);
            }
          });
          
          console.log('✅ Feedback deleted from Firestore');
        } catch (error) {
          console.warn('⚠️ Could not delete feedback:', error);
        }
      }
      return;
    }
    
    setThumbsFeedback(prev => ({
      ...prev,
      [storyUrl]: feedback
    }));
    
    if (!firestoreService?.isReady() || !storyData) {
      console.warn('⚠️ Service not ready or missing story data');
      return;
    }

    try {
      const aiAnalysis = analyzeArticleWithAI 
        ? await analyzeArticleWithAI(storyData.title, storyData.description)
        : null;
      
      const db = getFirestore();
      const feedbackRef = collection(
        db, 
        'users', 
        firestoreService.currentUser.uid, 
        'newsFeedback'
      );
      
      await addDoc(feedbackRef, {
        storyUrl,
        sourceId: storyData.sourceId || 'unknown',
        sourceName: storyData.sourceName || 'unknown',
        feedback,
        storyTitle: storyData.title,
        storyDescription: storyData.description || storyData.title,
        aiAnalysis,
        timestamp: serverTimestamp(),
        savedAt: new Date().toISOString(),
        articleUrl: storyData.source
      });
      
      console.log('✅ Feedback saved:', feedback);
      
    } catch (error) {
      console.error('❌ Failed to save feedback:', error);
      setThumbsFeedback(prev => {
        const updated = { ...prev };
        delete updated[storyUrl];
        return updated;
      });
    }
  };

  useEffect(() => {
    if (newsData && Object.keys(newsData).length > 0) {
      const hasValidData = selectedNewsFeeds.some(sourceId => 
        newsData[sourceId]?.stories?.length > 0
      );
      
      if (hasValidData) {
        console.log('✅ Valid data detected, forcing loaded state');
        setLoadingState('loaded');
        setRetryCount(0);
      }
    }
  }, [newsData, selectedNewsFeeds]);

  const validSources = useMemo(() => {
    if (!newsData || Object.keys(newsData).length === 0) {
      console.log('📭 No newsData available');
      return [];
    }
    
    const valid = selectedNewsFeeds.filter(sourceId => {
      const source = newsData[sourceId];
      const hasData = source && 
                      source.stories && 
                      Array.isArray(source.stories) &&
                      source.stories.length > 0;
      
      if (hasData) {
        console.log(`✅ ${sourceId}: ${source.stories.length} stories`);
      }
      
      return hasData;
    });
    
    if (valid.length > 0 && loadingState !== 'loaded') {
      console.log(`✅ Found ${valid.length} valid sources - setting loaded state`);
      setTimeout(() => setLoadingState('loaded'), 0);
    }
    
    return valid;
  }, [newsData, selectedNewsFeeds, loadingState]);

  useEffect(() => {
    const loadSavedFeedback = async () => {
      if (!firestoreService?.isReady()) {
        setIsLoadingFeedback(false);
        return;
      }

      try {
        const db = getFirestore();
        const feedbackRef = collection(
          db,
          'users',
          firestoreService.currentUser.uid,
          'newsFeedback'
        );
        
        const snapshot = await getDocs(feedbackRef);
        const savedFeedback = {};
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.storyUrl) {
            savedFeedback[data.storyUrl] = data.feedback;
          }
        });
        
        console.log('✅ Loaded saved feedback:', Object.keys(savedFeedback).length, 'items');
        setThumbsFeedback(savedFeedback);
        setIsLoadingFeedback(false);
        
      } catch (error) {
        console.error('❌ Failed to load feedback:', error);
        setIsLoadingFeedback(false);
      }
    };

    loadSavedFeedback();
  }, [firestoreService]);

  useEffect(() => {
    if (validSources.length > 0) {
      setRetryCount(0);
      setLoadingState('loaded');
    }
  }, [validSources.length]);

  // Mobile: Swipe handlers
  const goToNextSource = useCallback(() => {
    if (validSources.length === 0) return;
    setCurrentSourceIndex((prev) => (prev + 1) % validSources.length);
    setCurrentStoryIndex(0);
  }, [validSources.length]);

  const updateVisualState = useCallback(() => {
    const now = performance.now();
    if (now - lastUpdateTimeRef.current < 0.5) return;
    
    lastUpdateTimeRef.current = now;
    setVisualDragOffset(dragStateRef.current.dragOffset);
    setIsDraggingVisual(dragStateRef.current.isDragging);
  }, []);

  const handleSwipeComplete = useCallback(() => {
    goToNextSource();
    dragStateRef.current = {
      isDragging: false,
      dragStart: null,
      dragStartY: null,
      dragOffset: 0
    };
    setVisualDragOffset(0);
    setIsDraggingVisual(false);
  }, [goToNextSource]);

  const handleMouseDown = useCallback((e) => {
    if (!isMobile) return;
    if (e.button !== 0) return;
    dragStateRef.current.dragStart = e.clientX;
    dragStateRef.current.dragStartY = e.clientY;
    dragStateRef.current.isDragging = true;
    setIsDraggingVisual(true);
  }, [isMobile]);

  const handleMouseMove = useCallback((e) => {
    if (!isMobile) return;
    if (!dragStateRef.current.isDragging || dragStateRef.current.dragStart === null) return;
    
    const deltaX = e.clientX - dragStateRef.current.dragStart;
    const deltaY = Math.abs(e.clientY - dragStateRef.current.dragStartY);
    
    if (Math.abs(deltaX) > deltaY * 1.5) {
      dragStateRef.current.dragOffset = deltaX;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(updateVisualState);
    }
  }, [isMobile, updateVisualState]);

  const handleMouseUp = useCallback(() => {
    if (!isMobile) return;
    if (!dragStateRef.current.isDragging) return;
    
    if (Math.abs(dragStateRef.current.dragOffset) > 100) {
      handleSwipeComplete();
    } else {
      dragStateRef.current = {
        isDragging: false,
        dragStart: null,
        dragStartY: null,
        dragOffset: 0
      };
      setVisualDragOffset(0);
      setIsDraggingVisual(false);
    }
  }, [isMobile, handleSwipeComplete]);

  const handleTouchStart = useCallback((e) => {
    if (!isMobile) return;
    dragStateRef.current.dragStart = e.touches[0].clientX;
    dragStateRef.current.dragStartY = e.touches[0].clientY;
    dragStateRef.current.isDragging = true;
    setIsDraggingVisual(true);
  }, [isMobile]);

  const handleTouchMove = useCallback((e) => {
    if (!isMobile) return;
    if (!dragStateRef.current.isDragging || dragStateRef.current.dragStart === null) return;
    
    const deltaX = e.touches[0].clientX - dragStateRef.current.dragStart;
    const deltaY = Math.abs(e.touches[0].clientY - dragStateRef.current.dragStartY);
    
    if (Math.abs(deltaX) > deltaY * 1.5) {
      dragStateRef.current.dragOffset = deltaX;
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(updateVisualState);
    }
  }, [isMobile, updateVisualState]);

  const handleTouchEnd = useCallback(() => {
    if (!isMobile) return;
    if (!dragStateRef.current.isDragging) return;
    
    if (Math.abs(dragStateRef.current.dragOffset) > 100) {
      handleSwipeComplete();
    } else {
      dragStateRef.current = {
        isDragging: false,
        dragStart: null,
        dragStartY: null,
        dragOffset: 0
      };
      setVisualDragOffset(0);
      setIsDraggingVisual(false);
    }
  }, [isMobile, handleSwipeComplete]);

  // Desktop: Initialize story indices for each source
  useEffect(() => {
    if (isMobile) return;
    
    const initialIndices = {};
    validSources.forEach(sourceId => {
      if (!(sourceId in currentStoryIndices)) {
        initialIndices[sourceId] = 0;
      }
    });
    if (Object.keys(initialIndices).length > 0) {
      setCurrentStoryIndices(prev => ({ ...prev, ...initialIndices }));
    }
  }, [validSources, isMobile]);

  // Auto-advance stories
  useEffect(() => {
    if (validSources.length === 0 || loadingState !== 'loaded') return;
    
    if (isMobile) {
      // Mobile: advance current source only
      const currentSourceData = newsData[validSources[currentSourceIndex]];
      if (!currentSourceData) return;
      
      const interval = setInterval(() => {
        if (currentStoryIndex < 2) {
          setCurrentStoryIndex(prev => prev + 1);
        } else {
          goToNextSource();
        }
      }, 5000);
      
      return () => clearInterval(interval);
    } else {
      // Desktop: advance all sources independently
      const intervals = validSources.map(sourceId => {
        return setInterval(() => {
          setCurrentStoryIndices(prev => {
            const currentIndex = prev[sourceId] || 0;
            const sourceData = newsData[sourceId];
            if (!sourceData || !sourceData.stories) return prev;
            
            const nextIndex = currentIndex < 2 ? currentIndex + 1 : 0;
            return { ...prev, [sourceId]: nextIndex };
          });
        }, 5000);
      });
      
      return () => intervals.forEach(interval => clearInterval(interval));
    }
  }, [isMobile, validSources, newsData, loadingState, currentSourceIndex, currentStoryIndex, goToNextSource]);

  // Mobile: Mouse event listeners
  useEffect(() => {
    if (!isMobile) return;
    
    if (isDraggingVisual) {
      window.addEventListener('mousemove', handleMouseMove, { passive: true });
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isMobile, isDraggingVisual, handleMouseMove, handleMouseUp]);

  // Desktop: Scroll functionality
  const updateScrollButtons = useCallback(() => {
    if (isMobile || !scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) return;
    
    const container = scrollContainerRef.current;
    if (!container) return;

    updateScrollButtons();
    container.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [isMobile, updateScrollButtons, validSources.length]);

  const scroll = (direction) => {
    if (!scrollContainerRef.current) return;
    
    const scrollAmount = 400;
    const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
    
    scrollContainerRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (stuckDetectionRef.current) {
        clearTimeout(stuckDetectionRef.current);
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  const shouldShowShimmer = loadingState !== 'loaded' || validSources.length === 0;

  if (shouldShowShimmer) {
    if (isMobile) {
      return (
        <div style={{
          position: 'relative',
          width: '100%',
          height: '400px',
          perspective: '1000px',
          marginBottom: '20px'
        }}>
          <SkeletonSwipeCard isMobile={true} />
          {loadingState === 'error' && (
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fee',
              width: '320px',
              color: '#c00',
              padding: '8px 12px',
              borderRadius: '28px',
              fontSize: '12px',
              zIndex: 20,
              textAlign: 'center'
            }}>
              Failed: Please close app and come back again!
            </div>
          )}
          <style>{`
            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
        </div>
      );
    } else {
      return (
        <div style={{
          position: 'relative',
          width: '100%',
          padding: '20px 0',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            gap: '20px',
            overflowX: 'auto',
            padding: '0 20px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {[1, 2, 3].map(i => (
              <SkeletonSwipeCard key={i} isMobile={false} />
            ))}
          </div>
          {loadingState === 'error' && (
            <div style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              background: '#fee',
              color: '#c00',
              padding: '8px 12px',
              borderRadius: '28px',
              fontSize: '12px',
              zIndex: 20,
              textAlign: 'center'
            }}>
              Failed: Please close app and come back again!
            </div>
          )}
          <style>{`
            @keyframes shimmer {
              0% { background-position: 200% 0; }
              100% { background-position: -200% 0; }
            }
          `}</style>
        </div>
      );
    }
  }

  // Unified card rendering function
const renderCard = (sourceId, sourceData, story, storyIndex, { isCurrentCard, isMobile: cardIsMobile, style, handlers, cardPosition }) => {
  // ✅ Include cardPosition to make keys unique across the stack
  const uniqueKey = cardPosition 
    ? `${cardPosition}-${sourceId}-${storyIndex}-${story.source}`
    : `${sourceId}-${storyIndex}-${story.source}`;
  
  return (
    <div
      key={uniqueKey}
      style={style}
      {...handlers}
    >
        <div style={{
          width: '100%',
          height: '100%',
          background: 'rgba(255, 255, 255, 0.31)',
          backdropFilter: 'blur(20px)',
          borderRadius: '18px',
          padding: '4px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.93)'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            background: 'white',
            borderRadius: '15px',
            border: '1px solid rgba(213, 213, 213, 0.5)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Image Section */}
            <div style={{ 
              position: 'relative', 
              width: '100%', 
              height: '180px',
              flexShrink: 0
            }}>
              <img 
                src={story.image} 
                alt={story.title}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  userSelect: 'none'
                }}
                draggable="false"
              />
              
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '50px',
                background: 'linear-gradient(to top, rgba(0,0,0,0.5), transparent)'
              }} />

              {/* Top Bar */}
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                right: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(10px)',
                  padding: '7px',
                  borderRadius: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  width: '36px',
                  height: '36px'
                }}>
                  <img 
                    src={sourceData.logo} 
                    alt={sourceData.name}
                    style={{ width: '18px', height: '18px', objectFit: 'contain' }}
                  />
                </div>

                {isCurrentCard && (
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isArticleSaved(story.source)) {
                          const savedArticle = savedArticles?.find(a => a.source === story.source);
                          if (savedArticle?.id) {
                            console.log('🗑️ Removing article with ID:', savedArticle.id);
                            onRemoveArticle(savedArticle.id);
                          } else {
                            console.error('❌ Could not find saved article ID for:', story.source);
                          }
                        } else {
                          onSaveArticle(story, {
                            name: sourceData.name,
                            logo: sourceData.logo,
                            color: sourceData.color,
                          });
                        }
                      }}
                      style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        background: isArticleSaved(story.source)
                          ? 'rgba(254, 243, 199, 0.95)'
                          : 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(10px)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <Bookmark
                        size={14}
                        color={isArticleSaved(story.source) ? '#92400e' : '#ffffffff'}
                        fill={isArticleSaved(story.source) ? '#92400e' : 'none'}
                      />
                    </button>
                  </div>
                )}
              </div>

              {/* Bottom Bar - Thumbs & Page Number */}
              <div style={{
                position: 'absolute',
                bottom: '8px',
                left: '10px',
                right: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                {isCurrentCard ? (
                  <div style={{
                    display: 'flex',
                    gap: '7px',
                    alignItems: 'center',
                    height: '28px',
                    position: 'relative',
                    perspective: '1200px'
                  }}>
                    {/* Thumbs Up Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCardFeedback(story.source, 'up', {
                          ...story,
                          sourceId: sourceId,
                          sourceName: sourceData.name,
                          source: story.source
                        });
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(10px)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'opacity 0.3s ease-in-out',
                        opacity: getCardFeedback(story.source) === 'down' ? 0 : 1,
                        animation: getCardFeedback(story.source) === 'down' 
                          ? 'fadeBackIn 0.5s ease-in-out 2s forwards' 
                          : 'none',
                        position: 'relative',
                        overflow: 'visible'
                      }}
                    >
                      <ThumbsUp 
                        size={14}
                        color="white"
                        strokeWidth={2.5}
                        fill={getCardFeedback(story.source) === 'up' ? 'white' : 'none'}
                      />
                      
                      {getCardFeedback(story.source) === 'up' && (
                        <>
                          {[...Array(8)].map((_, i) => (
                            <div
                              key={`confetti-up-${i}`}
                              style={{
                                position: 'absolute',
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: ['#22c55e', '#10b981', '#84cc16', '#fbbf24'][i % 4],
                                top: '50%',
                                left: '50%',
                                animation: `confettiUp${i} 0.6s ease-out forwards`,
                                pointerEvents: 'none'
                              }}
                            />
                          ))}
                        </>
                      )}
                    </button>

                    {/* Thumbs Down Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setCardFeedback(story.source, 'down', {
                          ...story,
                          sourceId: sourceId,
                          sourceName: sourceData.name,
                          source: story.source
                        });
                      }}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(10px)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'opacity 0.3s ease-in-out',
                        opacity: getCardFeedback(story.source) === 'up' ? 0 : 1,
                        animation: getCardFeedback(story.source) === 'up' 
                          ? 'fadeBackIn 0.5s ease-in-out 2s forwards' 
                          : 'none',
                        position: 'relative',
                        overflow: 'visible'
                      }}
                    >
                      <ThumbsDown 
                        size={14}
                        color="white"
                        strokeWidth={2.5}
                        fill={getCardFeedback(story.source) === 'down' ? 'white' : 'none'}
                      />
                      
                      {getCardFeedback(story.source) === 'down' && (
                        <>
                          {[...Array(8)].map((_, i) => (
                            <div
                              key={`confetti-down-${i}`}
                              style={{
                                position: 'absolute',
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: ['#ef4444', '#f97316', '#fb923c', '#fbbf24'][i % 4],
                                top: '50%',
                                left: '50%',
                                animation: `confettiDown${i} 0.6s ease-out forwards`,
                                pointerEvents: 'none'
                              }}
                            />
                          ))}
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div />
                )}

                <div style={{
                  background: 'rgba(0, 0, 0, 0.6)',
                  backdropFilter: 'blur(10px)',
                  padding: '4px 9px',
                  borderRadius: '14px',
                  fontSize: '9px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  {storyIndex + 1}/3
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div style={{
              height: '192px',
              padding: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '7px',
              background: 'white'
            }}>
              <h3 style={{
                fontSize: '13px',
                fontWeight: '600',
                color: '#1f2937',
                lineHeight: '1.3',
                margin: 0,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textAlign: 'left',
                wordBreak: 'break-word'
              }}>
                {story.title}
              </h3>

              <p style={{
                fontSize: '10px',
                color: '#6b7280',
                lineHeight: '1.4',
                margin: 0,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textAlign: 'left',
                wordBreak: 'break-word'
              }}>
                {story.description}
              </p>

              {isCurrentCard && (
                <>
                  {/* Story Progress Dots */}
                  <div style={{
                    display: 'flex',
                    gap: '4px',
                    justifyContent: 'center',
                    marginTop: '6px'
                  }}>
                    {sourceData.stories.slice(0, 3).map((_, idx) => (
                      <div
                        key={idx}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (cardIsMobile) {
                            setCurrentStoryIndex(idx);
                          } else {
                            setCurrentStoryIndices(prev => ({ ...prev, [sourceId]: idx }));
                          }
                        }}
                        style={{
                          width: idx === storyIndex ? '16px' : '5px',
                          height: '5px',
                          borderRadius: '3px',
                          background: idx === storyIndex ? sourceData.color : '#d1d5db',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                      />
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div style={{
                    marginTop: 'auto',
                    display: 'flex',
                    gap: '8px'
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(story.source, '_blank');
                      }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: '#f3f4f6',
                        color: '#1f2937',
                        border: 'none',
                        borderRadius: '18px',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e5e7eb';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f3f4f6';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      Read Full Story
                    </button>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onTuneIn({ 
                          selectedSources: [sourceId],
                          newsData: { [sourceId]: sourceData }
                        });
                      }}
                      style={{
                        flex: 1,
                        padding: '10px',
                        background: '#000000',
                        color: 'white',
                        border: 'none',
                        borderRadius: '18px',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#1f2937';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#000000';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <Radio size={14} />
                      Tune In
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Mobile: Render swipeable card stack
  const renderMobileCard = (sourceIndex, stackPosition) => {
    const sourceId = validSources[sourceIndex];
    const sourceData = newsData[sourceId];
    
    if (!sourceData || !sourceData.stories || sourceData.stories.length === 0) {
      return null;
    }
    
    const storyIdx = sourceIndex === currentSourceIndex ? currentStoryIndex : 0;
    const story = sourceData.stories[storyIdx];
    if (!story) return null;

    const isCurrentCard = sourceIndex === currentSourceIndex;
    const isNextCard = stackPosition === 1;
    
    let zIndex, scale, translateY, cardOpacity;
    
    const swipeProgress = Math.min(Math.abs(visualDragOffset) / 150, 1);
    const rotation = visualDragOffset * 0.05;
    const opacity = isDraggingVisual ? Math.max(0.7, 1 - Math.abs(visualDragOffset) / 300) : 1;
    
    if (isCurrentCard) {
      zIndex = 10;
      scale = 1 - (swipeProgress * 0.05);
      translateY = 0;
      cardOpacity = opacity;
    } else if (isNextCard) {
      zIndex = 9;
      const baseScale = 0.95;
      const targetScale = 1;
      scale = baseScale + ((targetScale - baseScale) * swipeProgress);
      
      const baseY = 8;
      const targetY = 0;
      translateY = baseY - (baseY * swipeProgress);
      
      const baseOpacity = 0.7;
      const targetOpacity = 1;
      cardOpacity = baseOpacity + ((targetOpacity - baseOpacity) * swipeProgress);
    } else {
      zIndex = 9 - stackPosition;
      scale = 0.90 - ((stackPosition - 1) * 0.05);
      translateY = 8 + ((stackPosition - 1) * 8);
      cardOpacity = 0.5;
    }

    let transform;
    
    if (isCurrentCard && isDraggingVisual) {
      transform = `translate3d(calc(-50% + ${visualDragOffset}px), ${translateY}px, 0) rotate(${rotation}deg) scale(${scale})`;
    } else {
      transform = `translate3d(-50%, ${translateY}px, 0) scale(${scale})`;
    }

    const hasTransition = !isDraggingVisual;

    return renderCard(sourceId, sourceData, story, storyIdx, {
      isCurrentCard,
      isMobile: true,
          cardPosition: `mobile-${stackPosition}`, // ✅ Add this
      style: {
        position: 'absolute',
        top: 0,
        left: '50%',
        transform,
        width: 'calc(100% - 2px)',
        maxWidth: '390px',
        height: '370px',
        zIndex,
        opacity: cardOpacity,
        transition: hasTransition ? 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.4s ease-out' : 'none',
        cursor: isCurrentCard ? (isDraggingVisual ? 'grabbing' : 'grab') : 'default',
        pointerEvents: isCurrentCard ? 'auto' : 'none',
        willChange: 'transform, opacity',
        WebkitTransform: transform,
        backfaceVisibility: 'hidden',
        perspective: 1000,
        WebkitPerspective: 1000,
        transformStyle: 'preserve-3d'
      },
      handlers: isCurrentCard ? {
        onMouseDown: handleMouseDown,
        onTouchStart: handleTouchStart,
        onTouchMove: handleTouchMove,
        onTouchEnd: handleTouchEnd
      } : {}
    });
  };

  // Desktop: Render horizontal scroll card
const renderDesktopCard = (sourceId) => {
  const sourceData = newsData[sourceId];
  if (!sourceData || !sourceData.stories || sourceData.stories.length === 0) {
    return null;
  }

  const storyIndex = currentStoryIndices[sourceId] || 0;
  const story = sourceData.stories[storyIndex];
  if (!story) return null;

  return (
    <div
      key={`desktop-${sourceId}`} // ✅ This wrapper needs a unique key too
      style={{
        minWidth: '320px',
        maxWidth: '390px',
        width: '100%',
        flexShrink: 0
      }}
    >
      {renderCard(sourceId, sourceData, story, storyIndex, {
        isCurrentCard: true,
        isMobile: false,
        cardPosition: `desktop-${sourceId}`, // ✅ Add this
        style: {},
        handlers: {}
      })}
    </div>
  );
};
  // Render based on view mode
  if (isMobile) {
    const currentSourceId = validSources[currentSourceIndex];
    const currentSourceData = newsData[currentSourceId];
    
    if (!currentSourceData) return null;

    const currentStory = currentSourceData?.stories[currentStoryIndex];
    if (!currentStory) return null;

    return (
      <div style={{
        position: 'relative',
        width: '100%',
        height: '400px',
        perspective: '1000px',
        marginBottom: '30px'
      }}>
        {/* Render card stack - current + next 2 */}
        {[2, 1, 0].map((offset) => {
          const sourceIndex = (currentSourceIndex + offset) % validSources.length;
          return renderMobileCard(sourceIndex, offset);
        })}

        {/* Navigation Dots */}
        <div style={{
          position: 'absolute',
          bottom: '4px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '5px',
          alignItems: 'center'
        }}>
          {validSources.map((sourceId, idx) => {
            const sourceData = newsData[sourceId];
            return (
              <button
                key={sourceId}
                onClick={() => {
                  setCurrentSourceIndex(idx);
                  setCurrentStoryIndex(0);
                }}
                title={sourceData.name}
                style={{
                  width: idx === currentSourceIndex ? '22px' : '4px',
                  height: '5px',
                  borderRadius: '4px',
                  background: idx === currentSourceIndex ? '#272727' : '#b1b5b9ff',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  padding: 0
                }}
              />
            );
          })}
        </div>

        <style>{`
          @keyframes fadeBackIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }

          @keyframes confettiUp0 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-24px, -24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp1 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(0, -28px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp2 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(24px, -24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp3 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-28px, 0) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp4 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(28px, 0) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp5 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-24px, 24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp6 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(0, 28px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp7 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(24px, 24px) scale(1.5); opacity: 0; }
          }

          @keyframes confettiDown0 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-24px, -24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown1 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(0, -28px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown2 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(24px, -24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown3 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-28px, 0) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown4 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(28px, 0) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown5 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-24px, 24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown6 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(0, 28px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown7 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(24px, 24px) scale(1.5); opacity: 0; }
          }
        `}</style>
      </div>
    );
  } else {
    // Desktop horizontal scroll view
    return (
      <div style={{
        position: 'relative',
        width: '100%',
        marginBottom: '30px'
      }}>
        {/* Navigation Arrows */}
        {canScrollLeft && (
          <button
            onClick={() => scroll('left')}
            style={{
              position: 'absolute',
              left: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 100,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            }}
          >
            <ChevronLeft size={24} color="#1f2937" />
          </button>
        )}

        {canScrollRight && (
          <button
            onClick={() => scroll('right')}
            style={{
              position: 'absolute',
              right: '0',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 100,
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
            }}
          >
            <ChevronRight size={24} color="#1f2937" />
          </button>
        )}

        {/* Horizontal Scroll Container */}
        <div
          ref={scrollContainerRef}
          style={{
            display: 'flex',
            gap: '20px',
            overflowX: 'auto',
            overflowY: 'hidden',
            padding: '20px',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: 'x mandatory'
          }}
        >
          {validSources.map(sourceId => renderDesktopCard(sourceId))}
        </div>

        <style>{`
          @keyframes fadeBackIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }

          @keyframes confettiUp0 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-24px, -24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp1 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(0, -28px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp2 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(24px, -24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp3 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-28px, 0) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp4 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(28px, 0) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp5 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-24px, 24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp6 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(0, 28px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiUp7 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(24px, 24px) scale(1.5); opacity: 0; }
          }

          @keyframes confettiDown0 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-24px, -24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown1 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(0, -28px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown2 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(24px, -24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown3 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-28px, 0) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown4 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(28px, 0) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown5 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(-24px, 24px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown6 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(0, 28px) scale(1.5); opacity: 0; }
          }
          @keyframes confettiDown7 {
            0% { transform: translate(-50%, -50%) translate(0, 0) scale(0); opacity: 1; }
            100% { transform: translate(-50%, -50%) translate(24px, 24px) scale(1.5); opacity: 0; }
          }

          div::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    );
  }
};

export default SwipeableNewsCards;